import React, { useRef, useState, useEffect, useCallback } from 'react';
import './MarqueeText.css';

export interface MarqueeTextProps {
  /** The text content to display */
  children: React.ReactNode;
  /** Optional className applied to the outer container */
  className?: string;
  /** Speed in pixels per second (default: 30) */
  speed?: number;
  /** Gap in pixels between the looping copies (default: 48) */
  gap?: number;
}

/**
 * MarqueeText – Displays text normally when it fits, and applies a
 * continuous looping scroll when the text overflows its container.
 * The content is duplicated so the loop appears seamless.
 */
export const MarqueeText: React.FC<MarqueeTextProps> = ({
  children,
  className,
  speed = 30,
  gap = 48,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLSpanElement>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const scrollW = content.scrollWidth;
    const clientW = container.clientWidth;
    setContentWidth(scrollW);
    setIsOverflowing(scrollW > clientW + 1);
  }, []);

  useEffect(() => {
    measure();

    const ro = new ResizeObserver(() => measure());
    if (containerRef.current) ro.observe(containerRef.current);

    return () => ro.disconnect();
  }, [measure, children]);

  // Total distance for one full loop = content width + gap
  const loopDistance = contentWidth + gap;
  // Duration so scroll speed stays consistent
  const duration = isOverflowing ? loopDistance / speed : 0;

  return (
    <div
      ref={containerRef}
      className={`marquee-text ${isOverflowing ? 'marquee-text--scrolling' : ''} ${className || ''}`}
      style={
        isOverflowing
          ? {
              '--marquee-loop-distance': `-${loopDistance}px`,
              '--marquee-duration': `${duration}s`,
              '--marquee-gap': `${gap}px`,
            } as React.CSSProperties
          : undefined
      }
    >
      <span ref={contentRef} className="marquee-text__inner">
        {children}
      </span>
      {isOverflowing && (
        <span className="marquee-text__inner marquee-text__inner--clone" aria-hidden="true">
          {children}
        </span>
      )}
    </div>
  );
};

export default MarqueeText;
